import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
  getPrivacyConsent,
  savePrivacyConsent,
  type ConsentCategories,
} from '../../utils/privacyConsent';
import apiService from '../../services/ApiService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const CookiePreferencesModal: React.FC<Props> = ({ isOpen, onClose, onSaved }) => {
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const [personalization, setPersonalization] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const existing = getPrivacyConsent();
    setAnalytics(Boolean(existing?.categories.analytics));
    setAdvertising(Boolean(existing?.categories.advertising));
    setPersonalization(Boolean(existing?.categories.personalization));
  }, [isOpen]);

  const save = (categories: Partial<ConsentCategories>) => {
    const record = savePrivacyConsent(categories);
    void apiService.savePrivacyConsentRemote({
      consent_version: record.version,
      analytics: record.categories.analytics,
      advertising: record.categories.advertising,
      personalization: record.categories.personalization,
      gpc_signal: record.gpc,
      source: 'preferences',
    });
    onSaved?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Cookie preferences"
      description="Necessary storage cannot be turned off. Optional categories stay off until you allow them."
      size="md"
    >
      <div className="space-y-4 text-sm text-gray-300">
        <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
          <input type="checkbox" checked disabled className="mt-1" />
          <span>
            <span className="block text-white font-medium">Strictly necessary</span>
            Sign-in, security, and core preferences.
          </span>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10">
          <input
            type="checkbox"
            className="mt-1"
            checked={analytics}
            onChange={(e) => setAnalytics(e.target.checked)}
          />
          <span>
            <span className="block text-white font-medium">Analytics</span>
            Helps us understand product usage. First-party by default.
          </span>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10">
          <input
            type="checkbox"
            className="mt-1"
            checked={advertising}
            onChange={(e) => setAdvertising(e.target.checked)}
          />
          <span>
            <span className="block text-white font-medium">Advertising</span>
            Optional advertising storage. Off when Global Privacy Control is present.
          </span>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10">
          <input
            type="checkbox"
            className="mt-1"
            checked={personalization}
            onChange={(e) => setPersonalization(e.target.checked)}
          />
          <span>
            <span className="block text-white font-medium">Personalization</span>
            Optional personalization of non-essential experiences.
          </span>
        </label>
      </div>
      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <Button
          type="button"
          variant="primary"
          className="min-h-[44px]"
          onClick={() => save({ analytics, advertising, personalization })}
        >
          Save preferences
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="min-h-[44px]"
          onClick={() => save({ analytics: false, advertising: false, personalization: false })}
        >
          Reject optional
        </Button>
      </div>
    </Modal>
  );
};

export default CookiePreferencesModal;
